import { useState } from "react";
import { Box, Button, Typography, Stepper, Step, StepLabel, TextField, IconButton, DialogContent } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CustomDialog from "../../../common/custom-dialog/custom-dialog";
import WoocommerceIntegration from "./woocommerce-integration";
import { WoocommerceIntegrationConfig } from "../../../../interfaces/common/woocommerce";
import WoocommerceIntegrationConfigForm from "./integration-config";

interface IntroDialogProps {
  onClose: () => void;
  connectedShop: string | null;
  setConnectedShop: (s: string | null) => void;
  storeConfig: WoocommerceIntegrationConfig | null;
  setStoreConfig: (s: WoocommerceIntegrationConfig | null) => void;
  storeId: number | null;
}

const steps = [
  {
    title: "Generate REST API Keys",
    description:
      "Create a new WooCommerce REST API key in your store settings. Copy both the consumer key and consumer secret below — you’ll need them to connect your store.",
    image: "/woocommerce/generate-keys.png",
    nextEnabled: true,
    backEnabled: false,
  },
  {
    title: "Set Up the Integration",
    description:
      "Enter your WooCommerce shop domain along with the REST API consumer key and secret to establish a secure connection.",
    nextEnabled: false,
    backEnabled: true,
  },
  {
    title: "",
    description: "",
    nextEnabled: true,
    backEnabled: false,
  },
];

export const IntroDialog = ({ onClose, connectedShop, setConnectedShop, storeConfig, setStoreConfig, storeId }: IntroDialogProps) => {
   const finishingSetup = !!storeId
  
  const [activeStep, setActiveStep] = useState(finishingSetup ? steps.length -1 : 0);

  const handleNext = () => setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));

  return (
    <CustomDialog open={true} onClose={onClose} title="Set Up WooCommerce Integration" maxWidth="xl" fullWidth>
        <DialogContent sx={{padding: 4,}}>
            <Box sx={{ width: "100%", minHeight: 500, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            
            {/* Stepper Header */}
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map((step) => (
                <Step key={step.title}>
                <StepLabel>{step.title}</StepLabel>
                </Step>
            ))}
            </Stepper>

            {/* Step Content */}
            <Box sx={{ textAlign: "center", px: 3, margin: "auto" }}>
            <Typography variant="h6" gutterBottom>
                {steps[activeStep].title}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, textAlign: 'center' }} align="center">
                {steps[activeStep].description}
            </Typography>

            {steps[activeStep].image && <Box
                component="img"
                src={steps[activeStep].image}
                alt={steps[activeStep].title}
                sx={{
                width: "100%",
                maxWidth: 650,
                borderRadius: 2,
                boxShadow: 2,
                objectFit: "contain",
                mb: 3,
                }}
            />}

            {/* Step-specific content */}
            {activeStep === 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
                </Box>
            )}

            {
                activeStep == 1 &&  <Box maxWidth={500} margin={"auto"}><WoocommerceIntegration connectedShop={connectedShop} setConnectedShop={setConnectedShop} /></Box>
            }

            

            {activeStep === 2 && (
                <>
                    {storeConfig && <WoocommerceIntegrationConfigForm
                      value={storeConfig}
                      storeId={storeId}
                      updateValue={setStoreConfig}
                    />}
                </>
            )}
            </Box>

            {/* Navigation */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button  onClick={handleBack} disabled={!steps[activeStep].backEnabled}>
                Back
            </Button>
            {activeStep < steps.length - 1 ? (
                <Button disabled={!steps[activeStep].nextEnabled} variant="contained" onClick={handleNext}>
                Next
                </Button>
            ) : (
                <Button variant="contained" color="success" onClick={()=>{
                    onClose();
                    window.location.reload();
                }}>
                Finish
                </Button>
            )}
            </Box>
        </Box>

        </DialogContent>
     
    </CustomDialog>
  );
};
